import '../i18n';
import {useTranslation} from 'react-i18next';
import React from 'react';
import { QUERY, MUTATION } from '../gql-operations';
import { useMutation } from '@apollo/client';
import useNotificationSystem from '../components/Notifications/useNotificationSystem';
import { AuthContext } from '../util/context';
import { buildThreadVisibilityFilterInput } from '../util/threadVisibilityFilters';

const logFolderMove = (event, details) => {
    if (process.env.NODE_ENV !== 'production') {
        console.debug(`[FolderMove client] ${event}`, details);
    }
};

function useMoveFolderMutation() {
    const {t: lnT} = useTranslation();

    // `moveFolder` is the legacy endpoint and can leave its cross-parent
    // transaction waiting indefinitely. `placeFolder` is the maintained
    // folder-placement path used by the current sidebar interactions.
    const [placeFolder, { data }] = useMutation(MUTATION.PLACE_FOLDER);
    const { movedFolderNotification } = useNotificationSystem();

    const {
        state: { showDeletedMessages, folderThreadVisibilityFilter }
    } = React.useContext(AuthContext);

    const createRefetchQuery = (currentFolder, toFolder) => {
        const queries = [];
        const parentIds = new Set();

        // Moving a folder changes its old parent's children, not the moved
        // folder's own content. The previous code refetched currentFolder.id,
        // which left the sidebar cache stale after a successful mutation.
        if (currentFolder?.parent) {
            parentIds.add(String(currentFolder.parent));
        } else if (currentFolder) {
            queries.push({ query: QUERY.GET_HOME_FOLDERS });
        }
        if (toFolder?.id) {
            parentIds.add(String(toFolder.id));
        }

        parentIds.forEach(folderId => {
            queries.push({
                query: QUERY.GET_FOLDER_CONTENT,
                variables: {
                    data: {
                        folderId,
                        isTrash: showDeletedMessages,
                        ...buildThreadVisibilityFilterInput(folderThreadVisibilityFilter)
                    }
                }
            });
        });

        return queries;
    };

    const runThis = async (currentFolder, toFolder) => {
        let toFolderSend = toFolder ? toFolder.id : null;
        const refetchQueries = createRefetchQuery(currentFolder, toFolder);
        logFolderMove('requesting mutation', {
            folderId: currentFolder?.id,
            clientSourceParentId: currentFolder?.parent || null,
            requestedParentId: toFolderSend,
            refetchCount: refetchQueries.length
        });

        let response = await placeFolder({
            variables: {
                data: {
                    folderId: currentFolder.id,
                    parentId: toFolderSend,
                    // The dropdown has no sibling-position picker. Place at
                    // the start of the chosen parent; a drag can refine order.
                    afterFolderId: null
                }
            },
            refetchQueries
            // update: async (store, {data: {moveFolder}}) => {
            //     // TODO this only works if they are on the first level
            //     // would not work in subfolders
            //     const data = store.readQuery({
            //         query: GET_HOME_FOLDERS_QUERY
            //     })

            //     const newFolders = [...data.getHomeFolders.folders];

            //     console.log('all folders', newFolders)

            //     let index = newFolders.findIndex(obj => obj.id === currentFolder.id);

            //     console.log('did you find the index', index);

            //     if (index > -1) {
            //         newFolders.splice(index, 1);
            //     }

            //     console.log('all folders new ones', newFolders)

            //     const newData = {
            //         getHomeFolders: {
            //             folders: [...newFolders],
            //             cursor: data.getHomeFolders.cursor,
            //             __typename: data.getHomeFolders.__typename
            //         }
            //     }

            //     store.writeQuery({
            //         query: GET_HOME_FOLDERS_QUERY,
            //         data: newData
            //     })
            // }
        });
        logFolderMove('mutation response', {
            movedFolders: response?.data?.placeFolder?.filter(Boolean).map(folder => ({
                id: folder.id,
                parent: folder.parent || null,
                before: folder.before || null
            }))
        });
        movedFolderNotification(toFolder ? toFolder : { id: 'root', title: lnT('messages:root') });

        // NOT USED, BETTER MANAGEMENT ON OTHER PLACES
        // customAnalytics.folder_edit({
        //     'action': "Move",
        //     'destination': toFolder ? "Subfolder" : 'Root'
        // })

        // Preserve the legacy hook contract for existing callers.
        return {
            ...response,
            data: {
                ...response.data,
                moveFolder: response?.data?.placeFolder || []
            }
        };
    };

    return [runThis, { data }];
}

export default useMoveFolderMutation;
